/**
 * consoleFlowsApi — the console implementation of the shared builder seam
 * (``features/hive-flows/api/FlowsApiContext``).
 *
 * Every function delegates to the console client (session-cookie transport,
 * ``/platform/console/v1/flows/*``), so the REUSED builder pieces (palette,
 * node inspector's on-demand schema, pending approvals) talk to the console
 * backend while the product builder keeps its default product transport.
 */

import type * as React from "react";

import { type FlowsApi, FlowsApiProvider } from "@/features/hive-flows/api/FlowsApiContext";

import {
  approveConsoleFlowRun,
  fetchConsoleFlowRun,
  fetchConsoleFlowRuns,
  exportConsoleFlowRunXlsx,
  getConsoleFlowPalette,
  getConsoleFlowArticleGroups,
  getConsoleNodeSchema,
  getConsolePendingApprovals,
  rejectConsoleFlowRun,
  runConsoleFlow,
  runConsoleFlowWithUpload,
  sendConsoleSupportCaseReply,
} from "../api/client";

/** Query-key scope segment for every console-side builder cache entry. */
export const CONSOLE_FLOWS_SCOPE = "console";

export const consoleFlowsApi: FlowsApi = {
  scope: CONSOLE_FLOWS_SCOPE,
  getPalette: () => getConsoleFlowPalette(),
  getArticleGroups: (flowId) => getConsoleFlowArticleGroups(flowId),
  getNodeSchema: (nodeType, params) => getConsoleNodeSchema(nodeType, params),
  getPendingApprovals: () => getConsolePendingApprovals(),
  approveRun: (runId, edits) => approveConsoleFlowRun(runId, edits),
  rejectRun: (runId, reason) => rejectConsoleFlowRun(runId, reason),
  // Run lifecycle via the console transport (``/platform/console/v1/flows/*``).
  // The console run types are alias re-exports of the SAME ``@ando/core`` wire
  // shapes the seam declares, so no adaptation is needed.
  run: (flowId, triggerData) => runConsoleFlow(flowId, { trigger_data: triggerData }),
  runWithFile: (flowId, file) => runConsoleFlowWithUpload(flowId, file),
  getRun: (runId) => fetchConsoleFlowRun(runId),
  sendSupportCaseReply: (runId, body) => sendConsoleSupportCaseReply(runId, body),
  listRuns: (flowId) => fetchConsoleFlowRuns(flowId),
  exportRunXlsx: (runId) => exportConsoleFlowRunXlsx(runId),
};

/** Wrap any console surface that mounts shared builder pieces. */
export function ConsoleFlowsApiProvider({
  children,
}: {
  children: React.ReactNode;
}): React.JSX.Element {
  return <FlowsApiProvider api={consoleFlowsApi}>{children}</FlowsApiProvider>;
}
